import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarClock } from "lucide-react";
import { Button, ErrorNote, Field, SidePanel, inputClass } from "@/components/ui";
import { api, errorMessage } from "@/lib/api";
import { useDebounced } from "@/lib/queries";
import { lots as fmtLots, money } from "@/lib/format";
import type { ScheduledOpenItem, ScheduledOpenPayload } from "@/lib/types";

/*
 * OPERAZIONI PROGRAMMATE (26/10): il pannello «Programma apertura» e
 * l'etichetta «Programmata 03:00» della card.
 *
 * Componente NUOVO, non il form di Operazione: quello resta com'e'. I campi
 * sono gli stessi che il form manda alla rotta di apertura (simbolo, direzione,
 * lotto, moltiplicatore o rapporto, SL e TP in denaro), piu' giorno e ora in
 * ora italiana. Si salva il corpo cosi' com'e': all'ora il backend chiama la
 * rotta di apertura di sempre, e tutto cio' che oggi si calcola al clic si
 * calcola li'. L'anteprima qui sotto e' solo un'indicazione di adesso.
 */

export const scheduledKey = ["scheduled-opens"] as const;

/** Le programmate in attesa del tenant, per Fase: servono all'etichetta della card. */
export function usePendingScheduled(): Map<string, ScheduledOpenItem> {
  const q = useQuery({
    queryKey: [...scheduledKey, "PENDING"],
    queryFn: () => api.scheduledOpens.list({ status: "PENDING" }),
    refetchInterval: 30_000,
  });
  return new Map((q.data?.items ?? []).map((i) => [i.sessionId, i]));
}

const ROME = "Europe/Rome";
const romeParts = (d: Date) => {
  const f = new Intl.DateTimeFormat("en-CA", { timeZone: ROME, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(d);
  const v = (t: string) => f.find((p) => p.type === t)!.value;
  return { day: `${v("year")}-${v("month")}-${v("day")}`, time: `${v("hour")}:${v("minute")}` };
};

/** «Programmata 03:00», con gg/mm se non e' oggi (ora italiana). */
export function scheduledShortLabel(i: ScheduledOpenItem): string {
  const quando = romeParts(new Date(i.runAt));
  const oggi = romeParts(new Date()).day;
  const [, mm, gg] = quando.day.split("-");
  return `Programmata ${quando.day === oggi ? "" : `${gg}/${mm} `}${quando.time}`;
}

export function ScheduledBadge({ item }: { item: ScheduledOpenItem }) {
  return (
    <span
      className="inline-flex shrink-0 items-center gap-1 rounded-full border border-accent/40 px-1.5 py-0.5 text-[11px] tabular-nums text-ink"
      title={`Apertura programmata, ora italiana${item.status === "CONNECTING" ? ": connessione dei conti in corso" : ""}`}
    >
      <CalendarClock className="h-3 w-3" aria-hidden />
      {scheduledShortLabel(item)}
    </span>
  );
}

/** Quello che serve al pannello della Fase: dall'elenco di Operazione o da una riga della pagina. */
export interface ScheduleTarget {
  id: string;
  kind: "PB" | "BB";
  clientName: string;
  canonicalSymbol: string;
  propCurrency: string;
  hedgeRatio: string | null;
}

const norm = (v: string) => v.trim().replace(",", ".");
const isNum = (v: string) => /^\d+(\.\d+)?$/.test(norm(v));

/** Il prossimo quarto d'ora pieno, almeno 10 minuti avanti: un valore di partenza, non una scelta. */
function defaultWhen() {
  const d = new Date(Date.now() + 10 * 60_000);
  d.setMinutes(Math.ceil(d.getMinutes() / 15) * 15, 0, 0);
  return romeParts(d);
}

export function ScheduleOpenPanel({ target, existing, onClose }: { target: ScheduleTarget; existing?: ScheduledOpenItem; onClose: () => void }) {
  const qc = useQueryClient();
  const bb = target.kind === "BB";
  const iniziale = existing?.payload as Partial<ScheduledOpenPayload> | undefined;
  const quando = existing ? { day: existing.runAtLocal.slice(0, 10), time: existing.runAtLocal.slice(11, 16) } : defaultWhen();

  const [symbol, setSymbol] = useState(iniziale?.canonicalSymbol ?? target.canonicalSymbol);
  const [direction, setDirection] = useState<"LONG" | "SHORT">(iniziale?.direction ?? "LONG");
  const [lotsText, setLots] = useState(iniziale?.propLots ?? "");
  const [multText, setMult] = useState(iniziale?.brokerMultiplier ?? (bb ? target.hedgeRatio ?? "" : ""));
  const [slText, setSl] = useState(iniziale?.slMoney !== undefined ? String(iniziale.slMoney) : "");
  const [tpText, setTp] = useState(iniziale?.tpMoney !== undefined ? String(iniziale.tpMoney) : "");
  const [day, setDay] = useState(quando.day);
  const [time, setTime] = useState(quando.time);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const symbols = useQuery({ queryKey: ["sessions", "symbols", target.id], queryFn: () => api.sessions.symbols(target.id) });
  const usabili = (symbols.data?.symbols ?? []).filter((s) => !s.blocker).map((s) => s.canonicalSymbol);
  useEffect(() => {
    // Il simbolo scelto non e' fra gli apribili (es. predefinito non mappato): si lascia scegliere, senza preselezionare un altro.
    if (symbols.data && symbol && !usabili.includes(symbol) && usabili.length && !existing) setSymbol("");
  }, [symbols.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const multOk = bb ? multText === "" || isNum(multText) : isNum(multText);
  const fieldsOk = !!symbol && isNum(lotsText) && multOk && isNum(slText) && (tpText === "" || isNum(tpText)) && /^\d{4}-\d{2}-\d{2}$/.test(day) && /^\d{2}:\d{2}$/.test(time);

  const payload = (): ScheduledOpenPayload => ({
    direction,
    propLots: norm(lotsText),
    ...(multText !== "" ? { brokerMultiplier: norm(multText) } : {}),
    canonicalSymbol: symbol,
    slMoney: Number(norm(slText)),
    ...(tpText !== "" && Number(norm(tpText)) > 0 ? { tpMoney: Number(norm(tpText)) } : {}),
  });

  // L'anteprima di ADESSO, con la rotta di sempre in sola lettura: un'indicazione, non un impegno.
  const q = useDebounced({ fieldsOk, direction, lots: norm(lotsText), mult: norm(multText || target.hedgeRatio || ""), symbol, sl: norm(slText), tp: norm(tpText) }, 400);
  const preview = useQuery({
    // Fuori da `scheduledKey`: salvare invalida l'elenco, non deve aspettare l'anteprima.
    queryKey: ["schedule-open-preview", target.id, q],
    enabled: q.fieldsOk,
    retry: false,
    queryFn: () =>
      api.sessions.preview(target.id, {
        direction: q.direction,
        lots: q.lots,
        multiplier: q.mult,
        canonicalSymbol: q.symbol,
        slMoney: q.sl,
        ...(q.tp && Number(q.tp) > 0 ? { tpMoney: q.tp } : {}),
      }),
  });

  const salva = async () => {
    setBusy(true);
    setError(null);
    try {
      const body = { runAtLocal: `${day}T${time}`, payload: payload() };
      if (existing) await api.scheduledOpens.update(existing.id, body);
      else await api.scheduledOpens.create(target.id, body);
      await qc.invalidateQueries({ queryKey: [...scheduledKey] });
      onClose();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const cur = target.propCurrency;
  const p = preview.data;
  return (
    <SidePanel
      open
      title={existing ? "Modifica apertura programmata" : "Programma apertura"}
      subtitle={`${target.clientName} · all'ora scelta il gestionale connette i conti 3 minuti prima e apre questa operazione, come «Apri operazione».`}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>
            Chiudi
          </Button>
          <Button variant="primary" onClick={() => void salva()} disabled={busy || !fieldsOk}>
            {busy ? "Salvataggio…" : existing ? "Salva modifiche" : "Programma"}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-3">
        <Field label="Giorno (ora italiana)">
          {(id) => <input id={id} type="date" className={inputClass} value={day} onChange={(e) => setDay(e.target.value)} />}
        </Field>
        <Field label="Ora (ora italiana)">
          {(id) => <input id={id} type="time" className={inputClass} value={time} onChange={(e) => setTime(e.target.value)} />}
        </Field>
      </div>

      <Field label="Simbolo" hint={symbols.data && !usabili.length ? "Nessun simbolo apribile su questa Fase adesso." : undefined}>
        {(id) => (
          <select id={id} className={inputClass} value={symbol} onChange={(e) => setSymbol(e.target.value)}>
            {!symbol && <option value="">Scegli il simbolo</option>}
            {symbol && !usabili.includes(symbol) && <option value={symbol}>{symbol}</option>}
            {usabili.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        )}
      </Field>

      <fieldset>
        <legend className="mb-1.5 text-sm text-muted">Direzione {bb ? "del broker A" : "del prop"}</legend>
        <div className="inline-flex overflow-hidden rounded border border-line text-sm" role="radiogroup">
          {(["LONG", "SHORT"] as const).map((d) => (
            <button
              key={d}
              type="button"
              role="radio"
              aria-checked={direction === d}
              onClick={() => setDirection(d)}
              className={`px-4 py-1.5 ${direction === d ? "bg-raised font-medium text-ink" : "text-muted hover:text-ink"}`}
            >
              {d === "LONG" ? "Long" : "Short"}
            </button>
          ))}
        </div>
      </fieldset>

      <div className="grid grid-cols-2 gap-3">
        <Field label={bb ? "Lotto broker A" : "Lotto prop"}>
          {(id) => <input id={id} inputMode="decimal" className={inputClass} value={lotsText} onChange={(e) => setLots(e.target.value)} placeholder="1" />}
        </Field>
        <Field label={bb ? "Rapporto" : "Moltiplicatore"} hint={bb ? "Vuoto = quello della Fase." : undefined}>
          {(id) => <input id={id} inputMode="decimal" className={inputClass} value={multText} onChange={(e) => setMult(e.target.value)} placeholder={bb ? target.hedgeRatio ?? "" : "0,2"} />}
        </Field>
        <Field label={`Stop loss (${cur})`}>
          {(id) => <input id={id} inputMode="decimal" className={inputClass} value={slText} onChange={(e) => setSl(e.target.value)} />}
        </Field>
        <Field label={`Take profit (${cur})`} hint="Vuoto = nessun take profit.">
          {(id) => <input id={id} inputMode="decimal" className={inputClass} value={tpText} onChange={(e) => setTp(e.target.value)} />}
        </Field>
      </div>

      <section className="rounded border border-line p-3 text-sm">
        <p className="mb-2 text-xs text-faint">Anteprima di adesso, solo un'indicazione: all'ora di esecuzione lotti, punti e importi si ricalcolano.</p>
        {!fieldsOk ? (
          <p className="text-muted">Compila i campi per vedere l'anteprima.</p>
        ) : preview.isLoading ? (
          <p className="text-muted">Calcolo…</p>
        ) : preview.error ? (
          <p className="text-muted">Anteprima non disponibile adesso: {errorMessage(preview.error)}</p>
        ) : p?.planError ? (
          <p className="text-loss">{p.planError}</p>
        ) : p?.plan ? (
          <dl className="grid grid-cols-2 gap-x-3 gap-y-1 tabular-nums">
            <dt className="text-prop">{bb ? "Broker A" : "Prop"}</dt>
            <dd>{fmtLots(p.plan.prop.lots)} lotti</dd>
            <dt className="text-broker">{bb ? "Broker B" : "Broker"}</dt>
            <dd>{fmtLots(p.plan.broker.lots)} lotti</dd>
            {p.money && (
              <>
                <dt className="text-muted">Stop loss</dt>
                <dd>{money(p.money.atMarket.sl.prop, p.money.propCurrency)}</dd>
                <dt className="text-muted">Take profit</dt>
                <dd>{p.money.atMarket.tp.prop > 0 ? money(p.money.atMarket.tp.prop, p.money.propCurrency) : "nessuno"}</dd>
              </>
            )}
          </dl>
        ) : null}
      </section>

      {error && <ErrorNote>{error}</ErrorNote>}
    </SidePanel>
  );
}
